import { Logger } from 'loglevel';
import buildQuery from 'odata-query';
import ODataDsnFilterBuilder from './Filters/ODataDsnFilterBuilder';
import { FilterCondition } from '../types/filter.types';
import { DsnBilansResponse } from '../types/bilan.types';
import { DsnPaymentsResponse } from '../types/paiement.types';
import { DsnDetailsResponse } from '../types/dsn.types';
import { ENV } from '../config/env';

const apiBaseUrl = ENV.BACK_EDI_URL;

interface RequestDiagnostics {
  orderBy?: string;
}

const getRequestId = (headers: Headers): string | undefined =>
  headers.get('x-correlation-id') ??
  headers.get('x-request-id') ??
  headers.get('request-id') ??
  headers.get('traceparent') ??
  undefined;

// Service for interacting with the OData DSN API
export class ODataDsnsService {
  constructor(
    private readonly getAccessToken: () => Promise<string>,
    private readonly logger: Logger,
  ) {}

  // Fetch DSNs from the OData API
  // This method retrieves DSNs based on the provided query and page size.
  async fetchDsnList(
    filter: FilterCondition[],
    orderBy: string[],
    expand: string[],
    pageSize: number,
    currentPage: number,
    signal: AbortSignal,
  ) {
    const skip = (currentPage - 1) * pageSize;
    const odataFilterBuilder = new ODataDsnFilterBuilder(filter, this.logger);
    const filterQuery = odataFilterBuilder.buildOdataFilter();

    const query2 = buildQuery({
      filter: filterQuery,
      skip: skip,
      top: pageSize,
      count: true,
      orderBy,
      expand,
    });

    return this.fetchDsnListQuery(query2, pageSize, signal, orderBy);
  }

  async fetchDsnListQuery(
    query: string,
    pageSize: number,
    signal: AbortSignal,
    orderBy: string[] = [],
  ) {
    const extraHeaders: Array<[string, string]> = [['Prefer', `odata.maxpagesize=${pageSize}`]];
    const apiFetchUrl = `${apiBaseUrl}/odata/V4/Dsns${query}`;

    return await this.fetchGetQueryJson(apiFetchUrl, 'DsnList', signal, extraHeaders, {
      orderBy: orderBy.join(', '),
    });
  }

  // Fetch DSN details by instance ID
  // This method retrieves detailed information about a specific DSN instance.
  async fetchDsnDetails(instanceId: string): Promise<DsnDetailsResponse> {
    const odataQuery = buildQuery({
      filter: `InstanceId eq ${instanceId}`,
      expand: ['DsnTiersDeclarantObj', 'DsnEnvoiSageDirectObj', 'DsnTiersDeclareObj'],
    });

    const apiFetchUrl = `${apiBaseUrl}/odata/V4/Dsns${odataQuery}`;

    this.logger.info('🔍 Récupération des détails de la DSN');

    const response = await this.fetchGetQueryJson(apiFetchUrl, 'DsnDetails');

    return response;
  }

  // Fetch DSN bilan list
  // This method retrieves a paginated list of bilans associated with a specific DSN instance
  async fetchDsnBilanList(
    instanceId: string,
    pageSize: number,
    currentPage: number,
  ): Promise<DsnBilansResponse> {
    const skip = (currentPage - 1) * pageSize;
    const odataQuery = buildQuery({
      filter: `InstanceIdDsn eq ${instanceId}`,
      skip: skip,
      top: pageSize,
      count: true,
      expand: ['DsnTypeBilanObj'],
      format: 'json',
    });

    const apiFetchUrl = `${apiBaseUrl}/odata/V4/DsnBilans${odataQuery}`;

    this.logger.info('Fetching bilans:', {
      url: apiFetchUrl,
      dsnInstanceId: instanceId,
      page: currentPage,
      pageSize,
    });

    const response = await this.fetchGetQueryJson(apiFetchUrl, 'DsnBilans');

    return response;
  }

  // Fetch a single bilan by its own instance ID
  // This method retrieves the details of one bilan, independently of the DSN bilans list,
  // so a bilan can be resolved from a deep link without relying on the paginated list state.
  async fetchDsnBilanDetails(bilanId: string): Promise<DsnBilansResponse> {
    const odataQuery = buildQuery({
      filter: `InstanceId eq ${bilanId}`,
      expand: ['DsnTypeBilanObj'],
      format: 'json',
    });

    const apiFetchUrl = `${apiBaseUrl}/odata/V4/DsnBilans${odataQuery}`;

    this.logger.info('Fetching bilan details:', { url: apiFetchUrl, bilanId });

    const response = await this.fetchGetQueryJson(apiFetchUrl, 'DsnBilanDetails');

    return response;
  }

  // Fetch DSN payments list
  // This method retrieves a paginated list of payments associated with a specific DSN instance.
  async fetchDsnPaymentList(
    instanceId: string,
    pageSize: number,
    currentPage: number,
  ): Promise<DsnPaymentsResponse> {
    const skip = (currentPage - 1) * pageSize;
    const odataQuery = buildQuery({
      filter: `InstanceIdDsn eq ${instanceId}`,
      skip: skip,
      top: pageSize,
      count: true,
      format: 'json',
    });

    const apiFetchUrl = `${apiBaseUrl}/odata/V4/DsnPaiements${odataQuery}`;

    this.logger.info('Fetching paiements:', {
      url: apiFetchUrl,
      dsnInstanceId: instanceId,
      page: currentPage,
      pageSize,
    });

    const response = await this.fetchGetQueryJson(apiFetchUrl, 'DsnPaiements');

    return response;
  }

  private async fetchGetQuery(
    fetchUrl: string,
    method: string,
    signal: AbortSignal | null = null,
    extraHeaders: Array<[string, string]> = [],
    diagnostics: RequestDiagnostics = {},
  ) {
    //Fetching data from OData API
    const accessToken = await this.getAccessToken();
    const headers: Record<string, string> = {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    };

    // Ajout des headers supplémentaires
    for (const [key, value] of extraHeaders) {
      headers[key] = value;
    }

    const response = await fetch(fetchUrl, {
      method: 'GET',
      headers,
      signal,
    });

    const requestId = getRequestId(response.headers);
    const requestContext = {
      endpoint: new URL(response.url).pathname,
      status: response.status,
      ...diagnostics,
      ...(requestId ? { requestId } : {}),
    };

    this.logger.info(`Response ${method} fetch:`, requestContext);

    if (!response.ok) {
      await response.text();
      this.logger.warn(`Error ${method} fetch details:`, {
        status: response.status,
        statusText: response.statusText,
        ...diagnostics,
        ...(requestId ? { requestId } : {}),
      });
      const requestIdDetail = requestId ? ` (request ID: ${requestId})` : '';
      throw new Error(`HTTP error! ${method} fetch status: ${response.status}${requestIdDetail}`);
    }

    return response;
  }

  private async fetchGetQueryJson(
    fetchUrl: string,
    method: string,
    signal: AbortSignal | null = null,
    extraHeaders: Array<[string, string]> = [],
    diagnostics: RequestDiagnostics = {},
  ) {
    const response = await this.fetchGetQuery(fetchUrl, method, signal, extraHeaders, diagnostics);

    return await response.json();
  }
}
